/**
 * app-types — consolidated types, reducer, and user state for the web app.
 * Extracted from lib/demo/{task-machine,demo-user,mock-processor} so those
 * demo directories can be deleted. Pure logic only — no React, no fetches.
 */

// ── Task machine types ─────────────────────────────────────────────────────

export type TaskState =
  | "idle"
  | "submitted"
  | "clarifying"
  | "budget"
  | "tier"
  | "format"
  | "matching"
  | "selecting"
  | "confirmed"
  | "working"
  | "delivered"
  | "approved"
  | "rating"
  | "completed";

export type TaskCategory = "marketing" | "writing" | "data" | "code" | "general";

export interface ClarifyingQuestion {
  title: string;
  choices: { label: string; text: string }[];
}

export interface AttachedFile {
  name: string;
  icon: "file-pdf" | "file-xlsx";
}

export interface Competitor {
  name: string;
  description: string;
}

export interface TaskResult {
  title: string;
  summary: string;
  competitors?: Competitor[];
  recommendations: string;
  files: AttachedFile[];
  /**
   * Preview format threaded from the backend result.
   * 'html' | 'md' | 'csv' | 'json' | 'code' | 'binary' | 'text'
   * Used by ResultCard to branch rendering.
   */
  format?: string;
  /** Raw content (full) from backend preview.content. */
  rawContent?: string;
  /**
   * The user's chosen download format key from FormatSelector
   * (md | txt | docx | csv | html | tsx). Drives client-side download
   * packaging so the file extension matches what the user picked,
   * independent of the backend-inferred render `format`.
   */
  downloadFormat?: string;
  /**
   * The full web-client TaskResult — present only for real (non-demo) tasks.
   * ResultCard delegates rendering to ResultView when this is set.
   */
  webResult?: import("@/lib/web-client").TaskResult;
}

export interface DemoAgent {
  id: string;
  name: string;
  avatarInitials: string;
  avatarBg: string;
  rating: number;
  tasksCompleted: number;
  tags: string[];
  specialties: string[];
  basePrice: number;
  priceRange: string;
  status: "available" | "offline";
  description: string;
  categories: TaskCategory[];
  stats: { success: string; avgSpeed: string; disputes: string };
  reviews: { stars: number; timeAgo: string; text: string }[];
}

export interface TaskSession {
  id: string;
  description: string;
  category: TaskCategory;
  questions: ClarifyingQuestion[];
  answers: Record<number, string>;
  currentQIdx: number;
  budget: number | null;
  recommendedBudget: number;
  /** Selected agent tier — set after TierSelector. */
  selectedTier: "fast" | "deep" | null;
  /** Selected output format — set after FormatSelector. */
  selectedFormat: "html" | "tsx" | "md" | null;
  /** User's chosen download format key (md|txt|docx|csv|html|tsx) for packaging. */
  selectedDownloadFormat: string | null;
  topAgents: DemoAgent[];
  selectedAgent: DemoAgent | null;
  result: TaskResult | null;
  revisionCount: number;
  rating: number | null;
  feedback: string;
  createdAt: number;
  completedAt: number | null;
}

export interface ReducerState {
  phase: TaskState;
  task: TaskSession | null;
  workingProgress: number;
  workingStatus: string;
  errorMessage: string | null;
}

export type TaskAction =
  | { type: "SUBMIT_TASK"; description: string }
  | {
      type: "ANALYSIS_DONE";
      questions: ClarifyingQuestion[];
      recommendedBudget: number;
      category: TaskCategory;
    }
  | { type: "ANSWER_QUESTION"; answer: string }
  | { type: "SKIP_QUESTION" }
  | { type: "SKIP_ALL_QUESTIONS" }
  | { type: "GO_BACK_QUESTION" }
  | { type: "CONFIRM_BUDGET"; amount: number; category?: TaskCategory }
  | { type: "SELECT_TIER"; tier: "fast" | "deep" }
  | { type: "SELECT_FORMAT"; format: "html" | "tsx" | "md" | null; downloadKey: string | null }
  | { type: "MATCHING_DONE"; topAgents: DemoAgent[] }
  | { type: "SELECT_AGENT"; agent: DemoAgent }
  | { type: "PROGRESS_TICK"; progress: number; status: string }
  | { type: "DELIVER_RESULT"; result: TaskResult }
  | { type: "APPROVE" }
  | { type: "REQUEST_REVISION" }
  | { type: "RATE"; rating: number; feedback: string }
  | { type: "SKIP_RATING" }
  | { type: "COMPLETE" }
  | { type: "RESET" }
  | { type: "INSUFFICIENT_BALANCE"; needed: number; have: number }
  | { type: "CLEAR_ERROR" };

export const initialState: ReducerState = {
  phase: "idle",
  task: null,
  workingProgress: 0,
  workingStatus: "",
  errorMessage: null,
};

function makeId(): string {
  return `task-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function taskReducer(state: ReducerState, action: TaskAction): ReducerState {
  switch (action.type) {
    case "SUBMIT_TASK": {
      return {
        ...state,
        phase: "submitted",
        errorMessage: null,
        task: {
          id: makeId(),
          description: action.description,
          category: "general",
          questions: [],
          answers: {},
          currentQIdx: 0,
          budget: null,
          recommendedBudget: 25,
          selectedTier: null,
          selectedFormat: null,
          selectedDownloadFormat: null,
          topAgents: [],
          selectedAgent: null,
          result: null,
          revisionCount: 0,
          rating: null,
          feedback: "",
          createdAt: Date.now(),
          completedAt: null,
        },
      };
    }

    case "ANALYSIS_DONE": {
      if (!state.task) return state;
      return {
        ...state,
        phase: "clarifying",
        task: {
          ...state.task,
          questions: action.questions,
          recommendedBudget: action.recommendedBudget,
          category: action.category,
          currentQIdx: 0,
        },
      };
    }

    case "ANSWER_QUESTION": {
      if (!state.task) return state;
      const nextIdx = state.task.currentQIdx + 1;
      const answers = { ...state.task.answers, [state.task.currentQIdx]: action.answer };
      if (nextIdx >= state.task.questions.length) {
        return { ...state, phase: "budget", task: { ...state.task, answers, currentQIdx: nextIdx } };
      }
      return { ...state, task: { ...state.task, answers, currentQIdx: nextIdx } };
    }

    case "SKIP_QUESTION": {
      if (!state.task) return state;
      const nextIdx = state.task.currentQIdx + 1;
      if (nextIdx >= state.task.questions.length) {
        return { ...state, phase: "budget", task: { ...state.task, currentQIdx: nextIdx } };
      }
      return { ...state, task: { ...state.task, currentQIdx: nextIdx } };
    }

    case "SKIP_ALL_QUESTIONS": {
      if (!state.task) return state;
      return {
        ...state,
        phase: "budget",
        task: { ...state.task, currentQIdx: state.task.questions.length },
      };
    }

    case "GO_BACK_QUESTION": {
      if (!state.task || state.task.currentQIdx === 0) return state;
      return {
        ...state,
        task: { ...state.task, currentQIdx: state.task.currentQIdx - 1 },
      };
    }

    case "CONFIRM_BUDGET": {
      if (!state.task) return state;
      return {
        ...state,
        phase: "tier",
        errorMessage: null,
        task: {
          ...state.task,
          budget: action.amount,
          ...(action.category ? { category: action.category } : {}),
        },
      };
    }

    case "SELECT_TIER": {
      if (!state.task) return state;
      return {
        ...state,
        phase: "format",
        task: { ...state.task, selectedTier: action.tier },
      };
    }

    case "SELECT_FORMAT": {
      if (!state.task) return state;
      return {
        ...state,
        phase: "matching",
        task: { ...state.task, selectedFormat: action.format, selectedDownloadFormat: action.downloadKey },
      };
    }

    case "INSUFFICIENT_BALANCE": {
      return {
        ...state,
        phase: "budget",
        errorMessage: `Insufficient balance. You have $${action.have.toFixed(2)}, need $${action.needed.toFixed(2)}.`,
      };
    }

    case "CLEAR_ERROR": {
      return { ...state, errorMessage: null };
    }

    case "MATCHING_DONE": {
      if (!state.task) return state;
      return {
        ...state,
        phase: "selecting",
        task: { ...state.task, topAgents: action.topAgents },
      };
    }

    case "SELECT_AGENT": {
      if (!state.task) return state;
      return {
        ...state,
        phase: "working",
        workingProgress: 0,
        workingStatus: "",
        task: { ...state.task, selectedAgent: action.agent },
      };
    }

    case "PROGRESS_TICK": {
      return {
        ...state,
        workingProgress: action.progress,
        workingStatus: action.status,
      };
    }

    case "DELIVER_RESULT": {
      if (!state.task) return state;
      return {
        ...state,
        phase: "delivered",
        workingProgress: 100,
        task: { ...state.task, result: action.result },
      };
    }

    case "APPROVE": {
      if (!state.task) return state;
      return { ...state, phase: "approved" };
    }

    case "REQUEST_REVISION": {
      if (!state.task) return state;
      return {
        ...state,
        phase: "working",
        workingProgress: 0,
        workingStatus: "",
        task: { ...state.task, revisionCount: state.task.revisionCount + 1 },
      };
    }

    case "RATE": {
      if (!state.task) return state;
      return {
        ...state,
        phase: "completed",
        task: {
          ...state.task,
          rating: action.rating,
          feedback: action.feedback,
          completedAt: Date.now(),
        },
      };
    }

    case "SKIP_RATING": {
      if (!state.task) return state;
      return {
        ...state,
        phase: "completed",
        task: { ...state.task, completedAt: Date.now() },
      };
    }

    case "COMPLETE":
    case "RESET": {
      return { ...initialState };
    }

    default:
      return state;
  }
}

// ── Working status labels (moved from mock-processor) ─────────────────────

export function workingStatuses(category: TaskCategory): string[] {
  switch (category) {
    case "marketing":
      return ["Scanning the market…", "Mapping competitors…", "Drafting the strategy…", "Finalizing…"];
    case "writing":
      return ["Outlining sections…", "Drafting content…", "Polishing copy…", "Finalizing…"];
    case "data":
      return ["Loading the dataset…", "Cleaning rows…", "Running analysis…", "Finalizing…"];
    case "code":
      return ["Reading the code…", "Running static checks…", "Writing up findings…", "Finalizing…"];
    default:
      return ["Thinking through the task…", "Gathering sources…", "Drafting the result…", "Finalizing…"];
  }
}

// ── Hero task ID handoff (moved from mock-processor) ─────────────────────

let _heroTaskId: string | null = null;

export function setHeroTaskId(id: string | null): void {
  _heroTaskId = id;
}

export function consumeHeroTaskId(): string | null {
  const id = _heroTaskId;
  _heroTaskId = null;
  return id;
}

// ── User types (moved from demo-user) ─────────────────────────────────────

export interface CompletedTaskSummary {
  id: string;
  title: string;
  agentName: string;
  cost: number;
  rating: number | null;
  completedAt: number;
}

export type TrainingConsent = "pending" | "opted_in" | "opted_out";

export interface DemoUser {
  id: string;
  name: string;
  initials: string;
  balance: number;
  isSignedIn: boolean;
  taskHistory: CompletedTaskSummary[];
  /** Training-data consent — "pending" until the user picks in/out. */
  trainingConsent: TrainingConsent;
  /** Timestamp (ms) of the user's consent choice. 0 while pending. */
  trainingConsentAt: number;
  /** Count of anonymised rows contributed in live mode. */
  trainingContributions: number;
}

export const DEFAULT_USER: DemoUser = {
  id: "demo-user-1",
  name: "Demo User",
  initials: "DU",
  balance: 100,
  isSignedIn: false,
  taskHistory: [],
  trainingConsent: "pending",
  trainingConsentAt: 0,
  trainingContributions: 0,
};

const STORAGE_KEY = "botwork.demoUser";

export function loadUser(): DemoUser {
  if (typeof window === "undefined") return DEFAULT_USER;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_USER;
    const parsed = JSON.parse(raw) as Partial<DemoUser & { assets?: unknown[]; bookmarkedAgentIds?: string[] }>;
    return {
      ...DEFAULT_USER,
      ...parsed,
      taskHistory: Array.isArray(parsed.taskHistory) ? parsed.taskHistory : [],
      trainingConsent:
        parsed.trainingConsent === "opted_in" || parsed.trainingConsent === "opted_out"
          ? parsed.trainingConsent
          : "pending",
      trainingConsentAt: typeof parsed.trainingConsentAt === "number" ? parsed.trainingConsentAt : 0,
      trainingContributions:
        typeof parsed.trainingContributions === "number" ? parsed.trainingContributions : 0,
    };
  } catch {
    return DEFAULT_USER;
  }
}

export function saveUser(user: DemoUser): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
  } catch {
    /* storage unavailable — silently ignore */
  }
}
